import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const FONT_MAP = {
  free: "'Outfit', sans-serif",
  sans: "'Outfit', sans-serif",
  narrow: "'Arial Narrow', 'Outfit', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'JetBrains Mono', monospace",
  loma: "'Outfit', sans-serif",
};

const POS = {
  top: "items-start pt-8",
  center: "items-center",
  bottom: "items-end pb-14",
};

// style: caption style preset. text: caption string. t: 0..1 progress (player).
// loop: when true the component animates itself (used for wizard previews).
export default function AnimatedCaption({ style, text, t = 0, loop = false, className = "" }) {
  const [lt, setLt] = useState(0);
  const raf = useRef();
  useEffect(() => {
    if (!loop) return;
    let start = performance.now();
    const tick = (now) => {
      const p = ((now - start) / 2600) % 1;
      setLt(p);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [loop]);

  if (!style || !text) return null;
  const prog = loop ? lt : t;
  const anim = style.animation || "fade";
  const words = (style.uppercase ? text.toUpperCase() : text).split(" ");
  const font = FONT_MAP[style.font] || FONT_MAP.free;
  const color = style.color || "#FFFFFF";
  const hi = style.highlight || null;
  const hiText = style.highlight_text || "#111111";

  const base = {
    fontFamily: font,
    fontWeight: 800,
    color,
    textShadow: hi ? "none" : "0 2px 8px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.8)",
    lineHeight: 1.08,
    letterSpacing: style.uppercase ? "0.01em" : "0",
  };

  const renderWords = () => {
    if (anim === "karaoke") {
      const active = Math.min(words.length - 1, Math.floor(prog * words.length));
      return words.map((w, i) => (
        <span key={`${i}-${w}`} style={{
          padding: hi ? "0.02em 0.18em" : 0, margin: "0.06em",
          borderRadius: 8, display: "inline-block",
          backgroundColor: hi && i === active ? hi : "transparent",
          color: hi && i === active ? hiText : color,
          transform: i === active ? "scale(1.06)" : "scale(1)",
          transition: "all 0.12s ease",
        }}>{w}</span>
      ));
    }
    if (anim === "type") {
      const full = words.join(" ");
      const n = Math.max(1, Math.floor(prog * full.length));
      return <span>{full.slice(0, n)}<span style={{ opacity: prog < 1 ? 1 : 0 }}>▌</span></span>;
    }
    return words.map((w, i) => <span key={`${i}-${w}`} style={{ margin: "0.06em", display: "inline-block" }}>{w}</span>);
  };

  const container = {
    pop: { initial: { scale: 0.6, opacity: 0 }, animate: { scale: 1, opacity: 1 } },
    slide: { initial: { y: 40, opacity: 0 }, animate: { y: 0, opacity: 1 } },
    bounce: { initial: { y: -30, opacity: 0 }, animate: { y: 0, opacity: 1 } },
    fade: { initial: { opacity: 0 }, animate: { opacity: 1 } },
    karaoke: { initial: { opacity: 0 }, animate: { opacity: 1 } },
    type: { initial: { opacity: 1 }, animate: { opacity: 1 } },
  }[anim] || { initial: { opacity: 0 }, animate: { opacity: 1 } };

  return (
    <div className={`pointer-events-none absolute inset-0 flex justify-center px-4 text-center ${POS[style.position] || POS.bottom} ${className}`}>
      <motion.div
        key={`${anim}-${loop ? Math.floor(prog * 3) : 0}`}
        initial={container.initial}
        animate={container.animate}
        transition={{ type: anim === "bounce" ? "spring" : "tween", stiffness: 500, damping: 12, duration: 0.4 }}
        style={base}
        className="max-w-[92%] flex flex-wrap justify-center"
      >
        {renderWords()}
      </motion.div>
    </div>
  );
}
